import { SocialLoginType } from "../helpers/Constants";

export enum GA_EVENTS {
  signup = 'sign_up',
  signupProgress = 'signup_progress',
  signupError = 'signup_error',
  signupSuccess = 'signup_success',
  login = 'login',
  campaignDetails = 'campaign_details',
  shiftRequestSubmitted = 'shift_request_submitted',
  availabilityPublished = 'availability_published',
  invoiceGenerated = 'invoice_generated',
  replacementLiked = 'replacement_liked',
}

export enum AnalyticsLoginMethod {
  email = 'email',
  google = 'google',
  facebook = 'facebook',
  apple = 'apple',
  biometric = 'biometric',
}

export enum AnalyticsUserRole {
  professional = 'professional',
  pharmacy = 'pharmacy',
  pharmacyAdmin = 'pharmacy_admin',
  admin = 'admin',
}

export enum AnalyticsShiftType {
  shortTerm = 'short_term',
  longTerm = 'long_term',
  counterOffer = 'counter_offer',
}

export type LoginEvent = {
  event: GA_EVENTS.login;
  method: AnalyticsLoginMethod;
};

export type CampaignDetailsEvent = {
  event: GA_EVENTS.campaignDetails;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
};

export type ShiftRequestSubmittedEvent = {
  event: GA_EVENTS.shiftRequestSubmitted;
  shift_type: AnalyticsShiftType;
};

export type AvailabilityPublishedEvent = {
  event: GA_EVENTS.availabilityPublished;
  is_recurring: boolean;
};

export type InvoiceGeneratedEvent = {
  event: GA_EVENTS.invoiceGenerated;
  count: number;
};

export type ReplacementLikedEvent = {
  event: GA_EVENTS.replacementLiked;
  is_liked: boolean;
};

export type FeatureEvent =
  | LoginEvent
  | CampaignDetailsEvent
  | ShiftRequestSubmittedEvent
  | AvailabilityPublishedEvent
  | InvoiceGeneratedEvent
  | ReplacementLikedEvent;

// USER-SCOPED VALUES; NULL CLEARS THE PROPERTY
export type AnalyticsUserProperties = Record<string, string | null>;

export type SignupEvent = {
  event: GA_EVENTS.signup;
  method: SocialLoginType | null;
  step_number?: undefined;
  step_name?: undefined;
  error_message?: undefined;
  role: string;
};

export type SignupProgressEvent = {
  event: GA_EVENTS.signupProgress;
  method?: undefined;
  step_number: number;
  step_name: string;
  error_message?: undefined;
  role?: undefined;
};

export type SignupErrorEvent = {
  event: GA_EVENTS.signupError;
  method?: undefined;
  step_number?: undefined;
  step_name?: undefined;
  role?: undefined;
  error_message: string;
};

export type SignupSuccessEvent = {
  event: GA_EVENTS.signupSuccess;
  method?: undefined;
  step_number?: undefined;
  step_name?: undefined;
  role?: undefined;
  error_message?: string;
};

export type AnalyticsEvent =
  | SignupEvent
  | SignupErrorEvent
  | SignupProgressEvent
  | SignupSuccessEvent
  | FeatureEvent;

export abstract class IAnalyticsDefinition {
  analyticsInstance: any;

  abstract init(analytics: any): void;
  abstract setUser(id: string | null): void;
  abstract setUserProperties(properties: AnalyticsUserProperties): void;
  abstract setConsent(isGranted: boolean): void;
  abstract logCustomEvent(params: AnalyticsEvent): void;
}
